import { addMinutes, format, isValid, parseISO } from 'date-fns';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { Button, Select, Table, TextInput, Theme } from '../../components';
import DateInput from '../../components/inputs/DateInput';
import { FetchCrudData, GetTableDataOptions, Report, ReportField, SORT_ORDER, TableColumn } from '../../models';
import { SelectOption } from '../../models/SelectOption';
import { reportsActions, reportsSelectors } from '../../redux';
import { GET_REPORTS_FIELDS } from '../../services/ReportsService';
import { Network } from '../../utils';
import { HH_MM_REGEX, utc } from '../../utils/date';
import { ITEMS_PER_PAGE } from '../constants';

import './admin.scss';
import './AdminReports.scss';

export const REPORT_COLUMNS: TableColumn[] = [
  {
    label: 'lblStore',
    field: ReportField.STORE_NAME,
  },
  {
    label: 'lblDepartment',
    field: ReportField.DEPARTMENT_NAME,
  },
  {
    label: 'lblActiveTemplate',
    field: ReportField.TEMPLATE_NAME,
  },
  {
    label: 'lblAction',
    field: ReportField.ACTION,
  },
  {
    label: 'lblDate',
    field: ReportField.DATE,
  },
];

enum ReportAction {
  FOOD_ITEM_ENTERED = 'FOOD_ITEM_ENTERED',
  FOOD_ITEM_OVERTIME = 'FOOD_ITEM_OVERTIME',
  FOOD_ITEM_EXITED_WASTE = 'FOOD_ITEM_EXITED_WASTE',
  FOOD_ITEM_EXITED_EMPTY = 'FOOD_ITEM_EXITED_EMPTY',
  CHANGED_TEMPLATE = 'CHANGED_TEMPLATE',
}

const ACTION_TO_TRANSLATION: Record<ReportAction, string> = {
  FOOD_ITEM_ENTERED: 'lblReportsActionFoodItemEntered',
  FOOD_ITEM_OVERTIME: 'lblReportsActionFoodItemOvertime',
  FOOD_ITEM_EXITED_WASTE: 'lblReportsActionFoodItemExitedWaste',
  FOOD_ITEM_EXITED_EMPTY: 'lblReportsActionFoodItemExitedEmpty',
  CHANGED_TEMPLATE: 'lblReportsActionChangedTemplate',
};

const REPORT_ACTION_OPTIONS = Object.keys(ACTION_TO_TRANSLATION).map(
  (reportAction: ReportAction): SelectOption => ({
    label: ACTION_TO_TRANSLATION[reportAction],
    value: reportAction,
  }),
);

const AdminReports = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const reports = useSelector(reportsSelectors.getReports);
  const isLoading = useSelector(reportsSelectors.isLoading);
  const total = useSelector(reportsSelectors.getTotal);
  const filterOptions = useSelector(reportsSelectors.getFilterOptions);

  const [selectedStoreId, setSelectedStoreId] = useState<string>('');
  const [selectedDepartmentTemplateId, setSelectedDepartmentTemplateId] = useState<string>('');
  const [selectedFoodTrayTemplateId, setSelectedFoodTrayTemplateId] = useState<string>('');
  const [selectedReportAction, setSelectedReportAction] = useState<string>('');
  const [selectedStartDate, setSelectedStartDate] = useState<Date | null>(new Date());
  const [selectedEndDate, setSelectedEndDate] = useState<Date | null>(new Date());
  const [selectedStartTime, setSelectedStartTime] = useState<string>('');
  const [selectedEndTime, setSelectedEndTime] = useState<string>('');

  const [filters, setFilters] = useState<FetchCrudData>({
    offset: 0,
    limit: ITEMS_PER_PAGE,
    s: null,
    sort: [ReportField.DATE + ',' + SORT_ORDER.DESC],
  });
  const [showFilterSidebar, setShowFilterSidebar] = useState<boolean>(false);

  useEffect(() => {
    if (filters.offset === 0) {
      dispatch(reportsActions.getReports(filters));
    } else if (filters.offset < total) {
      dispatch(reportsActions.getMoreReports(filters));
    }
  }, [filters]);

  useEffect(() => {
    dispatch(reportsActions.getFilterOptions(selectedStoreId, selectedDepartmentTemplateId));
  }, [selectedStoreId, selectedDepartmentTemplateId]);

  useEffect(() => {
    if (selectedStoreId) {
      setSelectedDepartmentTemplateId('');
    }
  }, [selectedStoreId]);

  useEffect(() => {
    setSelectedFoodTrayTemplateId('');
  }, [selectedDepartmentTemplateId]);

  const renderTableCell = (value: string, field: string, item: Report) => {
    switch (field) {
      case 'date':
        let date = parseISO(value);
        date = addMinutes(date, date.getTimezoneOffset() - new Date().getTimezoneOffset());
        const isValidDate = isValid(date);
        return (
          <span className='date-time'>
            <span>{isValidDate && format(date, 'dd/MM/yyyy')}</span>
            <span>{isValidDate && format(date, 'H:mm')}</span>
          </span>
        );

      case 'action':
        if (item.actionSubject) {
          return (
            item.actionSubject +
            ' - ' +
            t(ACTION_TO_TRANSLATION[value], {
              actionSubject: item.actionSubject,
            })
          );
        } else {
          return t(ACTION_TO_TRANSLATION[value]);
        }

      default:
        return value || '-';
    }
  };

  const getData = (options: GetTableDataOptions) => {
    setFilters((filters): FetchCrudData => {
      return {
        ...filters,
        offset: filters.limit * options.page,
        sort: [options.sortColumn + ',' + options.sortOrder],
      };
    });
  };

  const clearFilters = () => {
    setSelectedStoreId('');
    setSelectedDepartmentTemplateId('');
    setSelectedFoodTrayTemplateId('');
    setSelectedReportAction('');
    setSelectedStartDate(null);
    setSelectedEndDate(null);
    setSelectedStartTime('');
    setSelectedEndTime('');
    setFilters(oldFilters => ({
      ...oldFilters,
      s: undefined,
    }));
  };

  const applyFilters = () => {
    let validFilters = true;
    const andFilters = [];

    if (selectedStoreId) {
      andFilters.push({ storeId: selectedStoreId });
    }

    if (selectedDepartmentTemplateId) {
      andFilters.push({ departmentId: selectedDepartmentTemplateId });
    }

    if (selectedFoodTrayTemplateId) {
      andFilters.push({ templateId: selectedFoodTrayTemplateId });
    }

    if (selectedReportAction) {
      andFilters.push({ action: selectedReportAction });
    }

    if (selectedStartDate) {
      const startDate = utc(
        new Date(selectedStartDate.getFullYear(), selectedStartDate.getMonth(), selectedStartDate.getDate(), 0, 0, 0),
      );
      andFilters.push({
        date: {
          $gte: startDate.toISOString(),
        },
      });
    }

    if (selectedStartTime) {
      if (selectedStartTime.match(HH_MM_REGEX)) {
        const timeParts = selectedStartTime.split(':');
        andFilters.push({
          minutesInDay: {
            $gte: parseInt(timeParts[0]) * 60 + parseInt(timeParts[1]) + new Date().getTimezoneOffset(), // UTC times. This could possibly give issues around midnight when the offset is large (eg: japan)
          },
        });
      } else {
        validFilters = false;
      }
    }

    if (selectedEndDate) {
      const endDate = utc(
        new Date(selectedEndDate.getFullYear(), selectedEndDate.getMonth(), selectedEndDate.getDate(), 23, 59, 59),
      );

      andFilters.push({
        date: {
          $lte: endDate.toISOString(),
        },
      });
    }

    if (selectedEndTime) {
      if (selectedEndTime.match(HH_MM_REGEX)) {
        const timeParts = selectedEndTime.split(':');
        andFilters.push({
          minutesInDay: {
            $lte: parseInt(timeParts[0]) * 60 + parseInt(timeParts[1]) + new Date().getTimezoneOffset(), // UTC times. This could possibly give issues around midnight when the offset is large (eg: japan)
          },
        });
      } else {
        validFilters = false;
      }
    }

    if (!validFilters) {
      return;
    }

    setFilters(oldFilters => ({
      ...oldFilters,
      offset: 0,
      s: {
        $and: andFilters,
      },
    }));
    document.querySelector('.content')?.scrollTo(0, 0);
  };

  const exportCsv = () => {
    window.open(
      Network.getUrlWithParams('reports/exportCsv', {
        sort: filters.sort,
        s: JSON.stringify(filters.s),
        fields: GET_REPORTS_FIELDS,
      }),
    );
  };

  const renderChangeLog = () => {
    return (
      <Table
        columns={REPORT_COLUMNS}
        renderTableCell={renderTableCell}
        data={reports}
        getData={getData}
        page={Math.floor(filters.offset / ITEMS_PER_PAGE)}
        isLoading={isLoading}
        defaultSortColumn={'date'}
        defaultSortOrder={SORT_ORDER.DESC}
        scrollContainerSelector='.content'
      />
    );
  };

  return (
    <div className='filter-sidebar-wrapper'>
      <div className='content'>
        <div className='sticky-header'>
          <div className='top-buttons'>
            <Button theme={Theme.SECONDARY} onClickCallback={exportCsv} icon='Export'>
              {t('lblExportTable')}
            </Button>
            <Button theme={Theme.SECONDARY} onClickCallback={() => setShowFilterSidebar(!showFilterSidebar)} icon='Filter'>
              {t('lblFilter')}
            </Button>
          </div>
          <h1 className='title'>{t('titleReports')}</h1>
        </div>
        <section>
          {renderChangeLog()}
        </section>
      </div>
      {showFilterSidebar && (
        <aside className='aside-filter'>
          <h2>{t('lblFilter')}</h2>
          <Button
            className='btn-close'
            theme={Theme.TRANSPARANT}
            icon='Close'
            onClickCallback={() => setShowFilterSidebar(false)}
          />
          <Select
            label='lblStore'
            options={[{ label: 'lblAllStores', value: '' }, ...(filterOptions?.stores || [])]}
            onChange={setSelectedStoreId}
            value={selectedStoreId}
          />
          <Select
            label='lblDepartment'
            options={[{ label: 'lblAllDepartments', value: '' }, ...(filterOptions?.departmentTemplates || [])]}
            onChange={setSelectedDepartmentTemplateId}
            value={selectedDepartmentTemplateId}
          />
          <Select
            label='lblFoodTrayTemplate'
            options={[{ label: 'lblAllFoodTrayTemplates', value: '' }, ...(filterOptions?.foodTrayTemplates || [])]}
            onChange={setSelectedFoodTrayTemplateId}
            value={selectedFoodTrayTemplateId}
            disabled={!selectedDepartmentTemplateId}
          />
          <Select
            label='lblReportAction'
            options={[{ label: 'lblAllReportActions', value: '' }, ...REPORT_ACTION_OPTIONS]}
            onChange={setSelectedReportAction}
            value={selectedReportAction}
          />
          <div>
            <DateInput label='lblStartDate' value={selectedStartDate} onChange={date => setSelectedStartDate(date as Date)} />
            <DateInput label='lblEndDate' value={selectedEndDate} onChange={date => setSelectedEndDate(date as Date)} />
            <div className='flex-column'>
              <TextInput
                label={t('lblStartTime')}
                placeholder='00:00'
                value={selectedStartTime}
                onChange={setSelectedStartTime}
              />
              <TextInput label={t('lblEndTime')} placeholder='23:59' value={selectedEndTime} onChange={setSelectedEndTime} />
            </div>
          </div>
          <Button className='btn-full-width' theme={Theme.INVERSE} onClickCallback={clearFilters}>
            {t('lblReset')}
          </Button>
          <Button className='btn-full-width' theme={Theme.SECONDARY} onClickCallback={applyFilters}>
            {t('lblApplyFilter')}
          </Button>
        </aside>
      )}
    </div>
  );
};

export default AdminReports;
